// client/src/components/pages/Settings/LexiStarDisplayRules/DisplayRulesSettings.tsx (ST-00276)
//
// Settings → LexiStar → Map colours & states, /settings/modules/lexistar/map-colours. Page
// permission lexistar.trips.view or lexistar.notifications.manage (config/pagePermissions);
// changing anything needs lexistar.notifications.manage (in-page; the API says so as can_manage).
//
// How the organisation's trucks look on Live View, the control room, Replay and the Trips view:
//   * Colours — an ordered list: the FIRST rule whose condition holds gives the truck its colour
//     (a palette token: light and dark values) and its words. Move a rule up to let it win.
//   * Marks — every mark rule whose condition holds adds a badge, a ring, a pulsing ring or a
//     dashed outline, the highest priority first; one colour cannot carry several states.
//   The marker's shape and icon always back the colour. Conditions are the notification rule
//   builder's rows over the fields the organisation's devices report (./ConditionRows).
// Live preview: the draft over the organisation's trucks now (GET /display-rules/preview; the pure
// evaluator, map/displayRules). Save writes a new version. When someone saved meanwhile (409
// stale_version) the draft stays: the page says so and offers their version (load it, replacing
// yours) or keeping yours (the next Save replaces theirs). History lists every version with who
// and when, and can load one into the editor; Reset goes back to the standard colours (a version
// too).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaArrowDown, FaArrowUp, FaHistory, FaPalette, FaPen, FaPlus, FaSyncAlt, FaTrash, FaUndo } from 'react-icons/fa';
import { useAuth } from '../../../../types/commonComponents';
import {
  displayErrorCode,
  displayErrorText,
  getDisplayCatalogue,
  getDisplayHistory,
  getDisplayPreview,
  getDisplayRules,
  getDisplayVersion,
  resetDisplayRules,
  saveDisplayRules,
} from '../../../../services/lexistarDisplayRules';
import type {
  DisplayCatalogue,
  DisplayPreview,
  DisplayRuleHistory,
  DisplayRuleSet,
} from '../../../../types/lexistar-display-rules';
import { ErrorBanner, LoadingState, NoPermissionPage, NoticeBanner, PageHeader, cardClasses, inputClasses } from '../../LexiStar/StatusBoard/atoms';
import { AssetMarkerIcon } from '../../../map/assetMarker';
import { ASSET_STATUS_CLASSES, type AssetStatusKey } from '../../../map/assetStatus';
import { MarkGlyph, MarkTag } from '../../../map/DisplayMarks';
import { formatMiningDateTime } from '../../../../utils/dateUtils';
import {
  DEFAULT_RULE_IDS,
  MARK_TEXT,
  OVERLAY_MARKS,
  TOKEN_TEXT,
  describeCondition,
  legendOf,
  sameRule,
  validateRules,
  type DisplayLayer,
  type DisplayRule,
  type DisplayToken,
  type OverlayMark,
} from '../../../map/displayRules';
import ConditionRows from './ConditionRows';
import {
  COLOUR_CHOICES,
  colourChoiceOf,
  insertRule,
  moveRule,
  newRule,
  previewOf,
  removeRule,
  replaceRule,
  tokenForColour,
} from './displayRulesLogic';

export const VIEW = 'lexistar.trips.view';
export const MANAGE = 'lexistar.notifications.manage';

const primaryButton =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50';
const secondaryButton =
  'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 hover:bg-slate-50 rounded-lg disabled:opacity-50';
const iconButton = 'touch-target p-1.5 rounded-md text-slate-500 hover:bg-slate-100 disabled:opacity-30';

// ── Pickers ─────────────────────────────────────────────────────────────────────────────────

// Each colour once, by its colour name, not by the status words it carries elsewhere (the
// ST-00276 review: the status words read as picking another status). See COLOUR_CHOICES.
function ColourPicker({
  value,
  onChange,
  disabled,
  label,
}: {
  value: DisplayToken | undefined;
  onChange: (t: DisplayToken) => void;
  disabled: boolean;
  label: string;
}) {
  const current = colourChoiceOf(value);
  return (
    <div role="radiogroup" aria-label={label} className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
      {COLOUR_CHOICES.map((c) => {
        const on = current === c;
        return (
          <button
            key={c.token}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(tokenForColour(c, value))}
            data-token={c.token}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left text-xs transition-colors disabled:opacity-60 ${
              on ? 'border-primary-500 ring-2 ring-primary-500 bg-surface' : 'border-slate-200 bg-surface hover:border-slate-300'
            }`}
          >
            <AssetMarkerIcon status={c.token as AssetStatusKey} size={22} className="flex-shrink-0" />
            <span className="min-w-0 truncate font-medium text-slate-700">{c.name}</span>
          </button>
        );
      })}
    </div>
  );
}

function MarkPicker({ value, onChange, disabled }: { value: OverlayMark | undefined; onChange: (m: OverlayMark) => void; disabled: boolean }) {
  return (
    <div role="radiogroup" aria-label="Mark" className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
      {OVERLAY_MARKS.map((m) => {
        const on = value === m;
        return (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={on}
            disabled={disabled}
            onClick={() => onChange(m)}
            data-mark={m}
            title={MARK_TEXT[m].hint}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border text-left text-xs transition-colors disabled:opacity-60 ${
              on ? 'border-primary-500 ring-2 ring-primary-500 bg-surface' : 'border-slate-200 bg-surface hover:border-slate-300'
            }`}
          >
            <MarkGlyph mark={m} token="breakdown" />
            <span className="font-semibold text-slate-800">{MARK_TEXT[m].label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── One rule ────────────────────────────────────────────────────────────────────────────────

function RuleEditor({
  rule,
  catalogue,
  disabled,
  onChange,
}: {
  rule: DisplayRule;
  catalogue: DisplayCatalogue | null;
  disabled: boolean;
  onChange: (next: DisplayRule) => void;
}) {
  const v = rule.visual;
  const setVisual = (patch: Partial<DisplayRule['visual']>) => onChange({ ...rule, visual: { ...v, ...patch } });
  const idp = `rule-${rule.id}`;
  return (
    <div className="space-y-4 pt-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={`${idp}-name`} className="block text-xs font-medium text-slate-600 mb-1">
            Name
          </label>
          <input
            id={`${idp}-name`}
            value={rule.name || ''}
            maxLength={60}
            disabled={disabled}
            onChange={(e) => onChange({ ...rule, name: e.target.value })}
            className={inputClasses}
          />
        </div>
        <div>
          <label htmlFor={`${idp}-label`} className="block text-xs font-medium text-slate-600 mb-1">
            {rule.layer === 'base' ? 'Words on the truck (optional)' : v.mark === 'badge' ? 'Badge words' : 'Words (optional)'}
          </label>
          <input
            id={`${idp}-label`}
            value={v.label || ''}
            maxLength={40}
            disabled={disabled}
            placeholder={rule.layer === 'base' && v.key ? TOKEN_TEXT[v.key].label : ''}
            onChange={(e) => setVisual({ label: e.target.value || undefined })}
            className={inputClasses}
          />
        </div>
      </div>
      {rule.layer === 'base' ? (
        <div>
          <p className="text-xs font-medium text-slate-600 mb-1">Colour</p>
          <ColourPicker value={v.key} disabled={disabled} label="Colour" onChange={(t) => setVisual({ key: t })} />
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-slate-600 mb-1">Mark</p>
            <MarkPicker value={v.mark} disabled={disabled} onChange={(m) => setVisual({ mark: m })} />
          </div>
          {v.mark !== 'dashed' && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-1">{v.mark === 'badge' ? 'Badge colour (optional)' : 'Ring colour'}</p>
              <ColourPicker value={v.key} disabled={disabled} label="Mark colour" onChange={(t) => setVisual({ key: t })} />
            </div>
          )}
          <div className="max-w-[10rem]">
            <label htmlFor={`${idp}-priority`} className="block text-xs font-medium text-slate-600 mb-1">
              Priority (higher first)
            </label>
            <input
              id={`${idp}-priority`}
              type="number"
              min={-100}
              max={100}
              value={rule.priority ?? 0}
              disabled={disabled}
              onChange={(e) => onChange({ ...rule, priority: Math.max(-100, Math.min(100, Math.round(Number(e.target.value) || 0))) })}
              className={inputClasses}
            />
          </div>
        </div>
      )}
      <div>
        <p className="text-xs font-medium text-slate-600 mb-1">When</p>
        <ConditionRows when={rule.when} catalogue={catalogue} disabled={disabled} onChange={(when) => onChange({ ...rule, when })} />
      </div>
    </div>
  );
}

function RuleCard({
  rule,
  first,
  last,
  open,
  canManage,
  catalogue,
  onToggleOpen,
  onChange,
  onMove,
  onRemove,
}: {
  rule: DisplayRule;
  first: boolean;
  last: boolean;
  open: boolean;
  canManage: boolean;
  catalogue: DisplayCatalogue | null;
  onToggleOpen: () => void;
  onChange: (next: DisplayRule) => void;
  onMove: (dir: -1 | 1) => void;
  onRemove: () => void;
}) {
  const v = rule.visual;
  const off = rule.enabled === false;
  const title = rule.name || rule.id;
  return (
    <li className={`px-4 py-3 ${off ? 'bg-slate-50' : ''}`} data-rule={rule.id}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <div className="flex items-center gap-1" role="group" aria-label={`Move ${title}`}>
          <button type="button" onClick={() => onMove(-1)} disabled={!canManage || first} aria-label={`Move ${title} up`} className={iconButton}>
            <FaArrowUp size={11} aria-hidden="true" />
          </button>
          <button type="button" onClick={() => onMove(1)} disabled={!canManage || last} aria-label={`Move ${title} down`} className={iconButton}>
            <FaArrowDown size={11} aria-hidden="true" />
          </button>
        </div>
        <span className="flex-shrink-0">
          {rule.layer === 'base' && v.key ? (
            <AssetMarkerIcon status={v.key as AssetStatusKey} size={24} />
          ) : v.mark ? (
            <span className="inline-flex w-6 justify-center">
              <MarkGlyph mark={v.mark} token={v.key ?? null} />
            </span>
          ) : null}
        </span>
        <div className="flex-1 min-w-[12rem]">
          <p className={`text-sm font-semibold ${off ? 'text-slate-500' : 'text-slate-900'}`}>
            {title}
            {DEFAULT_RULE_IDS.has(rule.id) && <span className="ml-2 text-[10px] font-medium text-slate-500 uppercase tracking-wide">standard</span>}
          </p>
          <p className="text-xs text-slate-600">
            <span className="text-slate-500">When </span>
            {describeCondition(rule.when)}
            <span className="text-slate-500"> → </span>
            {rule.layer === 'base' && v.key ? (
              <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded-full font-semibold ${ASSET_STATUS_CLASSES[v.key as AssetStatusKey].pill}`}>{v.label || TOKEN_TEXT[v.key].label}</span>
            ) : v.mark ? (
              <MarkTag overlay={{ mark: v.mark, token: v.key ?? null, label: v.label || rule.name || null, rule_id: rule.id }} />
            ) : null}
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={!off}
            disabled={!canManage}
            onChange={(e) => onChange({ ...rule, enabled: e.target.checked ? undefined : false })}
            className="h-4 w-4 rounded border-slate-300"
            aria-label={`${title} on`}
          />
          {off ? 'Off' : 'On'}
        </label>
        <button type="button" onClick={onToggleOpen} aria-expanded={open} className={secondaryButton}>
          <FaPen size={11} aria-hidden="true" />
          {open ? 'Close' : canManage ? 'Edit' : 'Details'}
        </button>
        {canManage && (
          <button type="button" onClick={onRemove} aria-label={`Remove ${title}`} className={`${iconButton} hover:text-red-600`}>
            <FaTrash size={12} aria-hidden="true" />
          </button>
        )}
      </div>
      {open && <RuleEditor rule={rule} catalogue={catalogue} disabled={!canManage} onChange={onChange} />}
    </li>
  );
}

// ── The page ────────────────────────────────────────────────────────────────────────────────

const sameList = (a: readonly DisplayRule[], b: readonly DisplayRule[]) =>
  a.length === b.length && a.every((r, i) => sameRule(r, b[i] as DisplayRule));

export default function DisplayRulesSettings() {
  const { hasPermission } = useAuth();
  const canView = hasPermission(VIEW) || hasPermission(MANAGE);
  const [set, setSet] = useState<DisplayRuleSet | null>(null);
  const [draft, setDraft] = useState<DisplayRule[]>([]);
  const [catalogue, setCatalogue] = useState<DisplayCatalogue | null>(null);
  const [preview, setPreview] = useState<DisplayPreview | null>(null);
  const [history, setHistory] = useState<DisplayRuleHistory | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [confirmReset, setConfirmReset] = useState(false);
  // Someone saved while this draft was being edited: their version (the draft is kept).
  const [conflict, setConflict] = useState<DisplayRuleSet | null>(null);

  /** On a 409: read their version and keep the draft; the banner offers both ways out. */
  const onConflict = async (what: string) => {
    try {
      const latest = await getDisplayRules();
      setConflict(latest);
      setError(
        `Someone changed the map colours while you were editing them (now version ${latest.version}${latest.updated_by ? `, by ${latest.updated_by}` : ''}). ${what}`
      );
    } catch (err) {
      setError(displayErrorText(err, 'The map colours changed meanwhile and could not be read again.'));
    }
  };

  const canManage = !!set?.can_manage && hasPermission(MANAGE);

  /** Read the rules in force (the editor starts from them); `keepMessage`: after a refusal. */
  const load = useCallback(async (keepMessage = false) => {
    if (!canView) return;
    try {
      const rules = await getDisplayRules();
      setSet(rules);
      setDraft(rules.rules);
      if (!keepMessage) setError(null);
    } catch (err) {
      setError(displayErrorText(err, 'Could not load the map colours.'));
    } finally {
      setLoading(false);
    }
  }, [canView]);

  const loadPreview = useCallback(() => {
    getDisplayPreview()
      .then(setPreview)
      .catch(() => setPreview(null));
  }, []);

  useEffect(() => {
    load();
    if (!canView) return;
    getDisplayCatalogue()
      .then(setCatalogue)
      .catch(() => setCatalogue(null));
    loadPreview();
  }, [load, loadPreview, canView]);

  const dirty = !!set && !sameList(draft, set.rules);
  const validation = useMemo(() => validateRules(draft), [draft]);
  const result = useMemo(() => (preview ? previewOf(draft, preview.assets) : null), [draft, preview]);
  const legend = useMemo(() => legendOf(draft), [draft]);
  const taken = useMemo(() => new Set(draft.map((r) => r.id)), [draft]);

  const edit = (next: DisplayRule[]) => {
    setDraft(next);
    setNotice(null);
  };

  const add = (layer: DisplayLayer) => {
    // A new colour rule starts on the first device reading the devices report (else the first field).
    const reading = catalogue?.fields.find((f) => f.group === 'reading' && f.available && f.id !== 'reading.speed_kmh');
    const first = reading?.id ?? catalogue?.fields.find((f) => f.available)?.id ?? 'context';
    const rule = newRule(layer, taken, first);
    edit(insertRule(draft, rule));
    setOpen(rule.id);
  };

  const save = async () => {
    if (!set) return;
    if (!validation.ok) {
      setError(validation.errors.join(' · '));
      return;
    }
    setBusy(true);
    try {
      const res = await saveDisplayRules({ version: set.version, rules: draft, note: note.trim() || null });
      setSet(res);
      setDraft(res.rules);
      setNote('');
      setError(null);
      setNotice(res.changed ? `Saved as version ${res.version}. Live View, the control room, Replay and the Trips view use it from their next refresh.` : 'Nothing changed.');
      if (showHistory) getDisplayHistory().then(setHistory).catch(() => undefined);
      loadPreview();
    } catch (err) {
      if (displayErrorCode(err) === 'stale_version') await onConflict('Your changes are still here, not saved.');
      else setError(displayErrorText(err, 'The map colours were not saved.'));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    if (!set) return;
    setBusy(true);
    setConfirmReset(false);
    try {
      const res = await resetDisplayRules({ version: set.version, note: note.trim() || null });
      setSet(res);
      setDraft(res.rules);
      setError(null);
      setNotice(res.changed ? `Back to the standard colours (version ${res.version}). Your rules stay in the history.` : 'You already use the standard colours.');
      if (showHistory) getDisplayHistory().then(setHistory).catch(() => undefined);
      loadPreview();
    } catch (err) {
      if (displayErrorCode(err) === 'stale_version') await onConflict('Nothing was reset.');
      else setError(displayErrorText(err, 'The map colours were not reset.'));
    } finally {
      setBusy(false);
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) getDisplayHistory().then(setHistory).catch((err) => setError(displayErrorText(err, 'Could not load the history.')));
  };

  const loadVersion = async (version: number) => {
    try {
      const v = await getDisplayVersion(version);
      edit(v.rules);
      setNotice(`Version ${version} is in the editor. Save to use it again.`);
    } catch (err) {
      setError(displayErrorText(err, 'Could not load that version.'));
    }
  };

  const header = (
    <PageHeader
      icon={FaPalette}
      title="Map colours & states"
      subtitle="How your trucks look on Live View, the control room, Replay and the Trips view."
    />
  );

  if (!canView) return <NoPermissionPage header={header} message="You do not have permission to view the map colours." />;

  const colours = draft.filter((r) => r.layer === 'base');
  const marks = draft.filter((r) => r.layer === 'overlay');
  const list = (rules: DisplayRule[]) => (
    <ul className="divide-y divide-slate-100">
      {rules.map((r, i) => (
        <RuleCard
          key={r.id}
          rule={r}
          first={i === 0}
          last={i === rules.length - 1}
          open={open === r.id}
          canManage={canManage}
          catalogue={catalogue}
          onToggleOpen={() => setOpen((o) => (o === r.id ? null : r.id))}
          onChange={(next) => edit(replaceRule(draft, r.id, next))}
          onMove={(dir) => edit(moveRule(draft, r.id, dir))}
          onRemove={() => edit(removeRule(draft, r.id))}
        />
      ))}
    </ul>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-5 pb-28">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        {conflict && (
          <div className={`${cardClasses} p-4 flex flex-wrap items-center gap-3`} role="alertdialog" aria-label="Someone else saved">
            <p className="text-sm text-slate-700 flex-1 min-w-[16rem]">
              Their version {conflict.version} is in force. Load it to see what they changed (your edits are discarded), or keep
              yours: saving then replaces theirs.
            </p>
            <button
              type="button"
              onClick={() => {
                setSet(conflict);
                setDraft(conflict.rules);
                setConflict(null);
                setError(null);
                setOpen(null);
              }}
              className={secondaryButton}
            >
              Load their version
            </button>
            <button
              type="button"
              onClick={() => {
                setSet(conflict);
                setConflict(null);
                setError(null);
              }}
              className={primaryButton}
            >
              Keep mine
            </button>
          </div>
        )}
        <p className="text-sm text-slate-600 max-w-3xl">
          Every truck gets <strong>one colour</strong>: the first colour rule whose condition holds, from the top. On top of it,{' '}
          <strong>marks</strong> — a badge, a ring, a pulsing ring or a dashed outline — show every other state that holds, the highest priority
          first. The truck’s shape and icon always back the colour, so nothing depends on colour alone. Conditions use only what your devices
          report.
        </p>

        {loading || !set ? (
          <LoadingState label="Loading the map colours…" />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-xs text-slate-500 mr-auto" data-testid="display-version">
                {set.version === 0
                  ? 'The standard colours (never changed).'
                  : `Version ${set.version}${set.is_default ? ' · the standard colours' : ''}${set.updated_at ? ` · changed ${formatMiningDateTime(set.updated_at)}` : ''}${set.updated_by ? ` by ${set.updated_by}` : ''}`}
              </p>
              <button type="button" onClick={toggleHistory} aria-expanded={showHistory} className={secondaryButton}>
                <FaHistory size={12} aria-hidden="true" /> History
              </button>
              {canManage && (
                <button type="button" onClick={() => setConfirmReset(true)} disabled={busy || set.is_default} className={secondaryButton}>
                  <FaUndo size={12} aria-hidden="true" /> Reset to the standard colours
                </button>
              )}
            </div>

            {confirmReset && (
              <div className={`${cardClasses} p-4 flex flex-wrap items-center gap-3`} role="alertdialog" aria-label="Reset to the standard colours">
                <p className="text-sm text-slate-700 flex-1 min-w-[16rem]">
                  Go back to the standard colours for everyone in your organisation? Your rules stay in the history: you can load them again.
                </p>
                <button type="button" onClick={reset} disabled={busy} className={primaryButton}>
                  Reset
                </button>
                <button type="button" onClick={() => setConfirmReset(false)} className={secondaryButton}>
                  Cancel
                </button>
              </div>
            )}

            {showHistory && (
              <section className={`${cardClasses} overflow-hidden`} aria-labelledby="display-history">
                <h2 id="display-history" className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-900">
                  History
                </h2>
                {!history ? (
                  <LoadingState label="Loading the history…" />
                ) : history.versions.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500">Never changed: your organisation uses the standard colours.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {history.versions.map((v) => (
                      <li key={v.version} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm" data-version={v.version}>
                        <span className="font-semibold text-slate-900 w-24">Version {v.version}</span>
                        <span className="text-slate-700">{v.action === 'reset' ? 'Back to the standard colours' : `${v.rule_count} rules`}</span>
                        <span className="text-slate-500">
                          {formatMiningDateTime(v.created_at)} · {v.created_by}
                        </span>
                        {v.note && <span className="text-slate-600 italic">“{v.note}”</span>}
                        <span className="ml-auto">
                          {v.current ? (
                            <span className="text-xs font-semibold text-primary-700">In use</span>
                          ) : canManage ? (
                            <button type="button" onClick={() => loadVersion(v.version)} className="text-xs font-semibold text-primary-700 hover:underline">
                              Load into the editor
                            </button>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-[1fr_20rem] gap-5 items-start">
              <div className="space-y-5 min-w-0">
                <section className={`${cardClasses} overflow-hidden`} aria-labelledby="display-colours">
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <h2 id="display-colours" className="text-sm font-semibold text-slate-900">
                      Colours
                    </h2>
                    <span className="text-xs text-slate-500">· the first that holds, from the top</span>
                    {canManage && (
                      <button type="button" onClick={() => add('base')} className={`${secondaryButton} ml-auto`}>
                        <FaPlus size={10} aria-hidden="true" /> Add a colour rule
                      </button>
                    )}
                  </div>
                  {list(colours)}
                </section>
                <section className={`${cardClasses} overflow-hidden`} aria-labelledby="display-marks">
                  <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-slate-100">
                    <h2 id="display-marks" className="text-sm font-semibold text-slate-900">
                      Marks
                    </h2>
                    <span className="text-xs text-slate-500">· every one that holds, highest priority first</span>
                    {canManage && (
                      <button type="button" onClick={() => add('overlay')} className={`${secondaryButton} ml-auto`}>
                        <FaPlus size={10} aria-hidden="true" /> Add a mark
                      </button>
                    )}
                  </div>
                  {marks.length ? list(marks) : <p className="px-4 py-3 text-sm text-slate-500">No marks.</p>}
                </section>
              </div>

              <aside className="space-y-5 lg:sticky lg:top-4" aria-label="Live preview">
                <section className={`${cardClasses} p-4 space-y-3`} aria-labelledby="display-preview">
                  <div className="flex items-center gap-2">
                    <h2 id="display-preview" className="text-sm font-semibold text-slate-900">
                      Your trucks now
                    </h2>
                    <button type="button" onClick={loadPreview} aria-label="Refresh the preview" className={`${iconButton} ml-auto`}>
                      <FaSyncAlt size={11} aria-hidden="true" />
                    </button>
                  </div>
                  {!result ? (
                    <p className="text-sm text-slate-500">No trucks to preview.</p>
                  ) : (
                    <>
                      <p className="text-xs text-slate-500">
                        {result.total} truck{result.total === 1 ? '' : 's'} with {dirty ? 'your changes' : 'these rules'}
                        {preview?.truncated ? ' (the first 2,000)' : ''}.
                      </p>
                      <ul className="space-y-1" data-testid="preview-counts">
                        {result.counts.map((c) => (
                          <li key={c.token} className="flex items-center gap-2 text-sm" data-preview-token={c.token}>
                            <AssetMarkerIcon status={c.token as AssetStatusKey} size={20} />
                            <span className="flex-1 text-slate-700">{TOKEN_TEXT[c.token].label}</span>
                            <span className="tabular-nums font-semibold text-slate-900">{c.n}</span>
                          </li>
                        ))}
                      </ul>
                      {result.marks.length > 0 && (
                        <ul className="space-y-1 pt-2 border-t border-slate-100">
                          {result.marks.map((m) => (
                            <li key={m.rule_id} className="flex items-center gap-2 text-sm" data-preview-mark={m.rule_id}>
                              <span className="flex-1 text-slate-700">{m.label}</span>
                              <span className="tabular-nums font-semibold text-slate-900">{m.n}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {dirty && (
                        <div className="pt-2 border-t border-slate-100" data-testid="preview-changed">
                          <p className="text-xs font-semibold text-slate-700 mb-1">
                            {result.changed.length === 0
                              ? 'No truck looks different with your changes.'
                              : `${result.changed.length} truck${result.changed.length === 1 ? '' : 's'} would look different:`}
                          </p>
                          <ul className="space-y-1">
                            {result.changed.slice(0, 8).map((c) => (
                              <li key={c.asset.asset_key} className="flex flex-wrap items-center gap-1.5 text-xs">
                                <span className="font-mono font-semibold text-slate-800">{c.asset.registration || c.asset.asset_key}</span>
                                <span className={`px-1.5 py-0.5 rounded-full ${ASSET_STATUS_CLASSES[c.before.base.token as AssetStatusKey].pill}`}>{c.before.base.label}</span>
                                <span aria-hidden="true">→</span>
                                <span className={`px-1.5 py-0.5 rounded-full ${ASSET_STATUS_CLASSES[c.after.base.token as AssetStatusKey].pill}`}>{c.after.base.label}</span>
                                {c.after.overlays
                                  .filter((o) => o.mark !== 'dashed')
                                  .map((o) => (
                                    <MarkTag key={o.rule_id} overlay={o} />
                                  ))}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </>
                  )}
                </section>
                <section className={`${cardClasses} p-4`} aria-labelledby="display-legend">
                  <h2 id="display-legend" className="text-sm font-semibold text-slate-900 mb-2">
                    Map key
                  </h2>
                  <ul className="space-y-1 text-xs text-slate-700">
                    {legend.map((e) => (
                      <li key={`${e.kind}-${e.id}`} className="flex items-center gap-2" data-legend={`${e.kind}:${e.id}`}>
                        {e.kind === 'overlay' && e.mark ? (
                          <span className="inline-flex w-5 justify-center">
                            <MarkGlyph mark={e.mark} token={e.token} />
                          </span>
                        ) : (
                          <AssetMarkerIcon status={(e.token || 'parked') as AssetStatusKey} size={18} />
                        )}
                        <span>
                          <span className="font-medium text-slate-800">{e.label}</span>
                          {e.kind !== 'base' && e.hint && <span className="text-slate-500"> · {e.hint}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              </aside>
            </div>
          </>
        )}
      </div>

      {canManage && dirty && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-surface/95 backdrop-blur" role="region" aria-label="Unsaved changes">
          <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 flex flex-wrap items-center gap-3">
            <p className="text-sm font-semibold text-slate-800">Unsaved changes</p>
            {!validation.ok && (
              <p className="text-xs text-red-700 basis-full order-last" role="alert">
                {validation.errors.slice(0, 3).join(' · ')}
                {validation.errors.length > 3 ? ` · and ${validation.errors.length - 3} more` : ''}
              </p>
            )}
            <label htmlFor="display-note" className="sr-only">
              What changed
            </label>
            <input
              id="display-note"
              value={note}
              maxLength={200}
              placeholder="What changed (optional, for the history)"
              onChange={(e) => setNote(e.target.value)}
              className={`${inputClasses} flex-1 min-w-[12rem] max-w-md`}
            />
            <button
              type="button"
              onClick={() => {
                edit(set?.rules || []);
                setOpen(null);
              }}
              disabled={busy}
              className={`${secondaryButton} ml-auto`}
            >
              Discard
            </button>
            <button type="button" onClick={save} disabled={busy || !validation.ok} className={primaryButton}>
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
